import {Directive, ElementRef, inject, input, effect, Renderer2} from '@angular/core';
import {AccessControlService, EvaluationResult} from '../security/access-control.service';
import {PortfolioSegmentDataContext} from '../data/portfolio-segment-data-context.service';
import {WjTab} from '@mescius/wijmo.angular2.nav';
import {Portfolio, Segment} from '../../api-client-kt/src';
import {SegmentWithPermissions} from '../data/portfolio-data-context.service';

type AccessControlAction = 'hide' | 'disable';

@Directive({
    selector: '[appAccessControl]',
    standalone: true
})
export class AccessControlDirective {

    permission = input.required<string>({alias: 'appAccessControl'});
    action = input<AccessControlAction>('hide', {alias: 'acAction'});

    private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    private wjTabElementRef = inject(WjTab, { optional: true });

    private accessControlService = inject(AccessControlService);
    private pfSegData = inject(PortfolioSegmentDataContext);

    constructor() {
        effect(() => {
            const portfolio = this.pfSegData.portfolio.value();
            const segment = this.pfSegData.activeSegment.value();
            const permission = this.permission();
            const action = this.action();

            if (!permission) {
                this.applyDenyAction(action);
                return;
            }

            const [entity, attribute] = permission.split('.');
            let allow: boolean;

            switch (entity) {
                case 'role':
                    allow = this.accessControlService.hasRole(attribute);
                    break;

                case 'lob':
                    allow = this.pfSegData.activeSegment.value()?.lobKey === attribute;
                    break;

                case 'segment':
                    allow = this.resolveSegmentAccess(portfolio, segment, attribute);
                    break;

                default:
                    console.error(`Unknown entity for access control: ${entity}`);
                    allow = false;
            }

            if (allow) {
                this.applyAllowAction(action);
            } else {
                this.applyDenyAction(action);
            }
        });
    }

    private resolveSegmentAccess(portfolio: Portfolio | undefined, segment: SegmentWithPermissions | undefined, attribute: string | undefined): boolean {
        if (!segment || !portfolio) {
            return false;
        } else if (!attribute) {
            return this.accessControlService.canAccessSegment(segment).allowed;
        } else {
            switch (attribute) {
                case 'edit': return this.accessControlService.canEditSegment(segment, portfolio).allowed;
                case 'status': return this.accessControlService.canEditSegmentStatus(segment, portfolio).allowed;
            }
        }
        console.error(`Unknown attribute for access control: ${attribute}`);
        return false;
    }

    private applyDenyAction(action: AccessControlAction): void {
        if (action === 'hide') {
            if (this.wjTabElementRef) {
                this.wjTabElementRef.isVisible = false;
            } else {
                this.elementRef.nativeElement.style.display = 'none';
            }
        } else if (action === 'disable') {
            if (this.wjTabElementRef) {
                this.wjTabElementRef.isDisabled = true;
            } else {
                this.elementRef.nativeElement.setAttribute('disabled', 'true');
            }
        }
    }

    private applyAllowAction(action: AccessControlAction): void {
        if (action === 'hide') {
            if (this.wjTabElementRef) {
                this.wjTabElementRef.isVisible = true;
            } else {
                this.elementRef.nativeElement.style.display = '';
            }
        } else if (action === 'disable') {
            if (this.wjTabElementRef) {
                this.wjTabElementRef.isDisabled = false;
            } else {
                this.elementRef.nativeElement.removeAttribute('disabled');
            }
        }
    }
}

